import { useEffect, useMemo, useState } from "react";

import type { DeviceType } from "@/components/filters/DeviceTypeFilter";
import { useRequestAnalyticsQuery } from "@/hooks/useRequestAnalyticsQuery";
import i18n from "@/i18n/i18n";
import { AnalyticsServiceV2 } from "@/services/features/AnalyticsServiceV2";
import { AnalyticsCardData, DateRange } from "@/services/StorePerformanceService";
import { Store } from "@/types/store";
import {
  CurrencyCode,
  formatCurrencyWithNoDecimals,
  getStoreCurrencyIdentifiers,
} from "@/utils/currency";
import { formatPercentFromWhole } from "@/utils/number";
import { createLowerIsBetterTrend, type LowerIsBetterTrend } from "@/utils/analyticsTrend";

export interface CantFindRequestsData {
  percentage: AnalyticsCardData;
  monetary: AnalyticsCardData;
  percentageTrend: CantFindRequestsTrend;
  monetaryTrend: CantFindRequestsTrend;
  percentageComparisonProgress?: string;
}

export type CantFindRequestsTrend = LowerIsBetterTrend;

interface CantFindRequestsRawData {
  cantFindRequests: number;
  totalRequests: number;
  rawValue: number;
  percent: number;
}

export const useCantFindRequests = (
  stores: Store[] | null,
  dateRange?: DateRange,
  deviceType?: DeviceType,
  comparisonDateRange?: DateRange,
  areaIds?: number[],
): CantFindRequestsData => {
  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  const [rawData, setRawData] = useState<CantFindRequestsRawData | null>(null);
  const [comparisonRawData, setComparisonRawData] = useState<CantFindRequestsRawData | null>();
  const [loading, setLoading] = useState(false);
  const [completedRequestKey, setCompletedRequestKey] = useState<string | null>(null);
  const [currencyCode, currencySymbol] = getStoreCurrencyIdentifiers(stores);
  const {
    beginRequest,
    comparisonEndDate,
    comparisonStartDate,
    hasComparisonRequestInputs,
    hasRequestInputs,
    requestKey,
    baseParams,
    endDate,
    startDate,
  } = useRequestAnalyticsQuery({
    stores,
    dateRange,
    deviceType,
    comparisonDateRange,
    areaIds,
  });

  useEffect(() => {
    const isCurrent = beginRequest();
    if (!requestKey) {
      setRawData(null);
      setComparisonRawData(null);
      setLoading(false);
      setCompletedRequestKey(null);
      return;
    }

    const fetchRange = async (
      rangeStartDate: string,
      rangeEndDate: string,
    ): Promise<CantFindRequestsRawData | null> => {
      const analyticsService = AnalyticsServiceV2.getInstance();
      const params: Parameters<typeof analyticsService.getRequestAnalytics>[0] = {
        ...baseParams!,
        startDate: rangeStartDate,
        endDate: rangeEndDate,
      };
      const totalResponse = await analyticsService.getRequestAnalytics(params);
      const totalRequests = totalResponse?.data?.totalRequests ?? 0;

      if (totalRequests <= 0) {
        return null;
      }

      const cantFindResponse = await analyticsService.getRequestAnalytics({
        ...params,
        requestStatus: "ITEM_NOT_FOUND",
      });
      const cantFindRequests = cantFindResponse?.data?.totalRequests ?? 0;
      const rawValue = Math.round(cantFindResponse?.data?.totalRequestValue ?? 0);
      return {
        cantFindRequests,
        totalRequests,
        rawValue,
        percent: (cantFindRequests / totalRequests) * 100,
      };
    };

    const fetchCantFindRequests = async () => {
      setLoading(true);
      setComparisonRawData(hasComparisonRequestInputs ? undefined : null);
      try {
        try {
          const currentData = await fetchRange(startDate, endDate);
          if (isCurrent()) setRawData(currentData);
        } catch (error) {
          console.error("Error fetching can't find requests:", error);
          if (isCurrent()) setRawData(null);
          return;
        }

        if (hasComparisonRequestInputs && comparisonStartDate && comparisonEndDate) {
          try {
            const comparisonData = await fetchRange(comparisonStartDate, comparisonEndDate);
            if (isCurrent()) setComparisonRawData(comparisonData);
          } catch (error) {
            console.error("Error fetching can't find requests comparison:", error);
            if (isCurrent()) setComparisonRawData(null);
          }
        }
      } finally {
        if (isCurrent()) {
          setLoading(false);
          setCompletedRequestKey(requestKey);
        }
      }
    };

    void fetchCantFindRequests();
  }, [
    beginRequest,
    comparisonEndDate,
    comparisonStartDate,
    hasComparisonRequestInputs,
    requestKey,
    baseParams,
  ]);

  const isRequestPending = hasRequestInputs && completedRequestKey !== requestKey;

  return useMemo(() => {
    const emptyCard: AnalyticsCardData = {
      previousValue: null,
      isPositive: true,
      pillText: "",
      subtitle: i18n.t("common.analytics.noDataForTimeframe"),
      value: "--",
      rawValue: null,
      loading: loading || isRequestPending,
    };

    if (!rawData) {
      return {
        percentage: emptyCard,
        monetary: emptyCard,
        percentageTrend: hasComparisonRequestInputs
          ? createLowerIsBetterTrend(
              null,
              comparisonRawData === undefined ? undefined : (comparisonRawData?.percent ?? null),
            )
          : { status: "unavailable" },
        monetaryTrend: hasComparisonRequestInputs
          ? createLowerIsBetterTrend(
              null,
              comparisonRawData === undefined ? undefined : (comparisonRawData?.rawValue ?? null),
            )
          : { status: "unavailable" },
      };
    }

    const value = formatCurrencyWithNoDecimals(
      rawData.rawValue,
      currencyCode as CurrencyCode,
      currencySymbol,
    );
    const formattedPercent = formatPercentFromWhole(rawData.percent, currentLanguage);

    return {
      percentage: {
        previousValue: null,
        value: formattedPercent,
        subtitle: i18n.t("common.analytics.itemRequestsValue", { value }),
        isPositive: rawData.cantFindRequests === 0,
        pillText: i18n.t("common.analytics.requestsProgress", {
          current: rawData.cantFindRequests,
          total: rawData.totalRequests,
        }),
        rawValue: rawData.percent,
        loading: loading || isRequestPending,
      },
      monetary: {
        previousValue: null,
        value,
        subtitle: i18n.t("common.analytics.requestsNotFound", { count: rawData.cantFindRequests }),
        isPositive: rawData.cantFindRequests === 0,
        pillText: i18n.t("common.analytics.percentOfTotal", { percent: formattedPercent }),
        rawValue: rawData.rawValue,
        loading: loading || isRequestPending,
      },
      percentageTrend: hasComparisonRequestInputs
        ? createLowerIsBetterTrend(
            rawData.percent,
            comparisonRawData === undefined ? undefined : (comparisonRawData?.percent ?? null),
          )
        : { status: "unavailable" },
      percentageComparisonProgress:
        comparisonRawData && comparisonRawData.totalRequests > 0
          ? i18n.t("common.analytics.requestsProgress", {
              current: comparisonRawData.cantFindRequests,
              total: comparisonRawData.totalRequests,
            })
          : undefined,
      monetaryTrend: hasComparisonRequestInputs
        ? createLowerIsBetterTrend(
            rawData.rawValue,
            comparisonRawData === undefined ? undefined : (comparisonRawData?.rawValue ?? null),
          )
        : { status: "unavailable" },
    };
  }, [
    comparisonRawData,
    currentLanguage,
    currencyCode,
    currencySymbol,
    hasComparisonRequestInputs,
    isRequestPending,
    loading,
    rawData,
  ]);
};
